import * as anime from 'animejs'
import { css, StyleSheet } from 'aphrodite'
import * as React from 'react'

import { UnitAction } from '../../engine/actions/action'
import EUnit, { UnitStatus } from '../../engine/unit'
import Bar from '../components/bar'
import { ICON_SIZE } from '../components/icon'
import UnitGlyph from '../components/unitGlyph'
import style from '../utils/style'
import transform from '../utils/transform'
import { HEX_SIZE } from './iso'

const SCALE_FACTOR = HEX_SIZE / ICON_SIZE

const styles = StyleSheet.create({
  unit: {
    transform: transform.scaleY(SCALE_FACTOR).scaleX(SCALE_FACTOR).string(),
    strokeWidth: '2%',
  },
  backBarStyle: {
    stroke: 'black',
    strokeWidth: '.5px',
    fill: 'black',
  },
  actionDoneBadge: {
    fill: style.darkGrey,
    stroke: style.gold,
    strokeWidth: '.8px',
  },
  actionDoneCheck: {
    fill: 'none',
    stroke: style.gold,
    strokeWidth: '1.2px',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
  statusBadgeLabel: {
    fill: style.darkGrey,
    fontFamily: 'VT323, monospace',
    fontSize: 7,
    textAnchor: 'middle',
    dominantBaseline: 'central',
    stroke: 'none',
  },
})

interface IProps {
  unit: EUnit
  playerFaction: string
}

export default class Unit extends React.Component<IProps, {}> {
  listeners: Array<(...args: any[]) => void>

  statusInfo(status: UnitStatus) {
    switch (status) {
      case UnitStatus.Burning:
        return { label: 'B', color: '#ff6b2d' }
      case UnitStatus.Slowed:
        return { label: 'S', color: '#7cc7ff' }
      case UnitStatus.Sleeping:
        return { label: 'Z', color: '#c8a8ff' }
      case UnitStatus.Guard:
        return { label: 'G', color: style.gold }
      default:
        return { label: '?', color: style.textColor }
    }
  }

  statusTurnsRemaining(expiration: number) {
    return Math.max(1, expiration)
  }

  statusMaxTurns(status: UnitStatus, expiration: number) {
    const max = this.props.unit.statusMax.get(status)
    return max === undefined
      ? this.statusTurnsRemaining(expiration)
      : max
  }

  statusWheel(status: UnitStatus, expiration: number) {
    const radius = 4.45
    const circumference = 2 * Math.PI * radius
    const remaining = this.statusTurnsRemaining(expiration)
    const max = this.statusMaxTurns(status, expiration)
    const progress = Math.max(0, Math.min(1, remaining / max))
    return {
      radius,
      dash: `${circumference * progress} ${circumference}`,
    }
  }

  shouldDisplayStatus(status: UnitStatus, expiration: number) {
    const unit = this.props.unit
    return expiration > 0
      || status === UnitStatus.Guard
      || unit.factionId === unit.game.currenFaction.id
  }

  constructor(props: IProps, ctx) {
    super(props, ctx)
    const { game } = this.props.unit
    this.listeners = [
      game.listen('action:perform', this.onPerformAction),
      game.listen('unit:takeDamage', this.onTakeDamage),
      game.listen('animation:skip', this.onSkipAnimation),
    ]
  }

  componentWillUnmount() {
    this.listeners.forEach(unsubscribe => unsubscribe())
  }

  onPerformAction = async (action: UnitAction) => {
    if (action.unit.id === this.props.unit.id) {
      anime({
        targets: [this.refs.main],
        translateY: '-10',
        direction: 'alternate',
        duration: 350,
        easing: 'easeOutQuad',
      })
    }
  }

  onSkipAnimation = async () => {
    const main = this.refs.main as any
    if (main) {
      (anime as any).remove(main)
      main.removeAttribute('style')
      main.removeAttribute('opacity')
    }
  }

  onTakeDamage = async (unit: EUnit) => {
    if (unit.id === this.props.unit.id) {
      const main = this.refs.main as any
      if (!main) {
        return
      }

      (anime as any).remove(main)
      main.removeAttribute('style')
      main.removeAttribute('opacity')

      anime({
        targets: [main],
        opacity: 0,
        direction: 'alternate',
        loop: 8,
        easing: 'easeInOutQuad',
        duration: 100,
        complete: () => {
          main.removeAttribute('style')
          main.removeAttribute('opacity')
        },
      })
    }
  }

  render() {
    const { playerFaction, unit } = this.props
    const barProps = {
      x: -5, height: 1, width: 10, backClasses: [styles.backBarStyle],
    }
    const actionDone = unit.factionId === playerFaction
      && unit.actionPerformed
    const statuses = Array.from(unit.status.entries())
      .filter(([status, expiration]) =>
        this.shouldDisplayStatus(status, expiration),
      )

    const unitStyle = {
      stroke: unit.faction.color,
    }
    return (
      <g>
        <g ref="main">
          <g className={css(styles.unit)} style={unitStyle}>
            <UnitGlyph unitType={unit.type} />
          </g>
          <g transform="rotate(-90)">
            <Bar
              {...barProps}
              y={10}
              fill={style.hpColor}
              value={unit.hp / unit.type.hp}
            />
            <Bar
              {...barProps}
              y={11}
              fill={style.mpColor}
              value={unit.mp / unit.type.mp}
            />
            {unit.type.mana > 0 && <Bar
              {...barProps}
              y={12}
              fill={style.manaColor}
              value={unit.mana / unit.type.mana}
            />}
          </g>
          {actionDone && (
            <g transform="translate(9 -13)">
              <circle
                r="3.5"
                className={css(styles.actionDoneBadge)}
              />
              <path
                d="M -1.8 0 L -0.4 1.5 L 2 -1.6"
                className={css(styles.actionDoneCheck)}
              />
            </g>
          )}
          {statuses.map(([status, expiration], idx) => {
            const statusInfo = this.statusInfo(status)
            const wheel = this.statusWheel(status, expiration)
            return (
              <g transform={`translate(${-10 + idx * 8} -13)`} key={status}>
                <circle
                  r="4"
                  fill={statusInfo.color}
                  stroke="black"
                  strokeWidth=".45"
                />
                <circle
                  r={wheel.radius}
                  fill="none"
                  stroke="rgba(0, 0, 0, 0.45)"
                  strokeWidth="1.1"
                />
                <circle
                  r={wheel.radius}
                  fill="none"
                  stroke={style.textColor}
                  strokeWidth="1.1"
                  strokeDasharray={wheel.dash}
                  transform="rotate(-90)"
                />
                <text className={css(styles.statusBadgeLabel)}>
                  {statusInfo.label}
                </text>
              </g>
            )
          })}
        </g>
      </g>
    )
  }
}



// WEBPACK FOOTER //
// ./src/ui/stageView/unit.tsx